import { supabase } from './supabase';
import { db } from './db';
import { humanise } from './format';
import type { OutboxEntity } from '@/types/models';

/**
 * Did the server keep what was sent?
 *
 * A save can be accepted and still not keep a field: a trigger that
 * protects a column, or an RPC that does not update one (upsert_diary and
 * the diary date, before 0046). Without this check the screen says
 * "saved", the device keeps the value the person typed, and the server
 * quietly keeps the old one.
 *
 * Deliberately narrow: only the fields that place a record — its dates and
 * times, machine and shift. Plenty of other columns are legitimately set by
 * the server (a store part's description and cost, a labour rate, tidied
 * text), and a warning that fires on those would soon be ignored. Columns a
 * trigger owns by design are listed per table below.
 */

const SERVER_OWNED = new Set([
  'id', 'created_at', 'created_by', 'updated_at', 'updated_by', 'deleted_at',
  'synced_at', 'approved_at', 'client_rev', 'site_id',
]);

/** Placing fields a trigger sets by design (0013, 0037, 0038, 0042). */
const DERIVED: Record<string, readonly string[]> = {
  job_cards: ['opened_at', 'started_at', 'completed_at', 'closed_at'],
  breakdowns: ['ended_at', 'breakdown_date'],
  fuel_tanks: ['machine_id'],
  fuel_receipts: ['booked_on'],
  machines: ['retired_on'],
};

const PLACING = /(_date|_on|_at|datetime)$/;
const PLACING_KEYS = new Set(['machine_id', 'shift_id', 'period_start', 'period_end']);

export function unkeptFields(
  table: string,
  sent: Record<string, unknown> | null | undefined,
  saved: Record<string, unknown> | null | undefined,
): string[] {
  if (!sent || !saved) return [];
  const derived = DERIVED[table] ?? [];
  const out: string[] = [];
  for (const [key, value] of Object.entries(sent)) {
    if (!(PLACING.test(key) || PLACING_KEYS.has(key))) continue;
    if (SERVER_OWNED.has(key) || derived.includes(key) || !(key in saved)) continue;
    if (value === null || value === undefined || value === '') continue;
    if (!same(value, saved[key])) out.push(key);
  }
  return out;
}

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (b === null || b === undefined) return false;

  if (typeof a === 'object' || typeof b === 'object') {
    return stable(a) === stable(b);
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return String(a) === String(b);
  }

  const sa = String(a).trim();
  const sb = String(b).trim();

  // numeric(…) comes back as a number or a string, rounded to its scale.
  if (/^-?\d+(\.\d+)?$/.test(sa) && /^-?\d+(\.\d+)?$/.test(sb)) {
    return Math.abs(Number(sa) - Number(sb)) <= 0.005 + 1e-9;
  }

  // Timestamps: the form sends local ISO, the server answers in UTC.
  if (sa.includes('T') && sb.includes('T')) {
    const ta = Date.parse(sa);
    const tb = Date.parse(sb);
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) return Math.abs(ta - tb) < 1000;
  }

  // Text the triggers tidy (trimmed, spaces collapsed, upper-cased plates).
  const norm = (s: string) => s.replace(/\s+/g, ' ').toLowerCase();
  return norm(sa) === norm(sb);
}

function stable(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`;
}

/** "Diary date, Shift" from ['diary_date', 'shift_id']. */
export function fieldList(keys: string[]): string {
  return keys.map((k) => humanise(k.replace(/_id$/, ''))).join(', ');
}

export function unkeptMessage(keys: string[], record = 'the record'): string {
  return `Saved, but the server kept its own value for: ${fieldList(keys)}. `
    + `Open ${record} again to see what is stored; if it is wrong, ask a manager to correct it.`;
}

/* ------------------------------------------------------------------ */
/* Putting the server's copy back on the device                        */
/* ------------------------------------------------------------------ */

/** Outbox entity -> Postgres table. Kept here and in sync.ts in step. */
export const ENTITY_TABLE: Record<OutboxEntity, string> = {
  diary: 'daily_diary',
  breakdown: 'breakdowns',
  breakdown_part: 'breakdown_parts',
  weather_event: 'weather_events',
  weighbridge_ticket: 'weighbridge_tickets',
  blast: 'blasts',
  service_record: 'service_records',
  liner_installation: 'liner_installations',
  part_stock_movement: 'part_stock_movements',
  attachment: 'attachments',
  concrete_delivery: 'concrete_deliveries',
  concrete_customer: 'concrete_customers',
  vehicle_check: 'vehicle_checks',
  fuel_issue: 'fuel_issues',
  fuel_receipt: 'fuel_receipts',
  fuel_dip: 'fuel_dips',
  plant_production: 'plant_production',
  plant_stock_move: 'plant_stock_moves',
};

/** Child tables replaced together with their header. */
const CHILDREN: Partial<Record<OutboxEntity, Array<{ table: string; key: string }>>> = {
  diary: [
    { table: 'diary_time_allocations', key: 'diary_id' },
    { table: 'diary_activities', key: 'diary_id' },
  ],
  vehicle_check: [{ table: 'vehicle_check_items', key: 'check_id' }],
};

/**
 * Replace this device's copy of one record (and its detail lines) with the
 * server's. Used when a change the server refused is discarded: without it
 * the refused value stays on screen, and no later sync corrects it because
 * the server row has not changed.
 *
 * Returns false when the server could not be reached; the local copy is
 * then left alone rather than guessed at.
 */
export async function restoreFromServer(entity: OutboxEntity, id: string): Promise<boolean> {
  const table = ENTITY_TABLE[entity];
  if (!table) return false;

  const { data, error } = await supabase.from(table).select('*').eq('id', id).maybeSingle();
  if (error) return false;

  const children = CHILDREN[entity] ?? [];
  const childRows: Record<string, Array<Record<string, unknown>>> = {};
  for (const c of children) {
    const res = await supabase.from(c.table).select('*').eq(c.key, id).is('deleted_at', null);
    if (res.error) return false;
    childRows[c.table] = (res.data ?? []) as Array<Record<string, unknown>>;
  }

  const tables = [table, ...children.map((c) => c.table)].map((t) => db.table(t));
  await db.transaction('rw', tables, async () => {
    const row = data as Record<string, unknown> | null;
    if (row && !row.deleted_at) await db.table(table).put(row);
    else await db.table(table).delete(id);

    for (const c of children) {
      await db.table(c.table).where(c.key).equals(id).delete();
      if (row && childRows[c.table].length) await db.table(c.table).bulkPut(childRows[c.table]);
    }
  });
  return true;
}
